import { clearUserDrafts } from './draft-keys';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * AuthProvider — mock or WorkOS-backed, selected at build time.
 *
 * When the Vite env has VITE_WORKOS_CLIENT_ID set we run in "WorkOS mode":
 *   - Session lives in an HttpOnly cookie set by the Worker at
 *     /api/auth/callback. The SPA hydrates its user via GET /api/auth/me.
 *   - signIn() navigates to /api/auth/authorize (Worker → WorkOS hosted UI).
 *   - signOut() POSTs to /api/auth/logout (Worker clears the cookie).
 *
 * Otherwise we fall back to the localStorage-only mock so contributors can
 * iterate offline without a WorkOS tenant. Any email works in mock mode.
 *
 * We intentionally do NOT mount <AuthKitProvider> from @workos-inc/authkit-react
 * here. That provider owns client-side session state, which conflicts with the
 * cookie-based server session model. The package remains in dependencies as
 * an escape hatch if we ever want to expose AuthKit client helpers directly.
 *
 * The user model carries a `profile_id` because most pages need one; the
 * onboarding wizard populates it after profile creation. In WorkOS mode we
 * also persist profile_id in localStorage so it survives page refreshes
 * without adding a users.profile_id column (see setProfileId below).
 */

export type MockUser = {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'editor' | 'viewer';
  profile_id?: string;
  /**
   * V101-6-3: the workspace slug `profile_id` belongs to. A profile is
   * per workspace; WorkspaceGate re-hydrates it when the URL's workspace
   * differs, so no page reads (or writes) another workspace's profile.
   */
  profile_slug?: string;
  /**
   * The user's landing workspace, sourced from GET /api/auth/me. Used
   * by the legacy `/app/*` → `/w/:slug/app/*` redirect. Null when the
   * user has no memberships yet (fresh signup, pre-fork).
   */
  default_workspace?: {
    workspace_id: string;
    slug: string;
    name: string;
  } | null;
};

type AuthContextValue = {
  user: MockUser | null;
  /** True until the initial /api/auth/me probe returns (WorkOS mode only). */
  loading: boolean;
  /**
   * WorkOS mode ⇒ redirect to /api/auth/authorize (with optional
   * `?login_hint=<email>` so AuthKit routes the user straight to their
   * configured IdP). Mock mode ⇒ stash a fake user.
   */
  signIn: (email?: string, name?: string) => void;
  /**
   * ADR-0015 · /signup is a synonym for /signin with the sign-up form
   * pre-selected. Passes `screen_hint=sign-up` to WorkOS AuthKit so the
   * hosted UI opens on the sign-up tab. Mock mode falls through to
   * signIn (no distinction there).
   */
  signUp: (email?: string, name?: string) => void;
  signOut: () => void;
  /** Set the active profile; `slug` = the workspace it belongs to (V101-6-3). */
  setProfileId: (profileId: string | undefined, slug?: string) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

// Security review info item: per-user browser drafts are cleared on sign-out.
export { AUTHOR_RULE_DRAFT_PREFIX, clearUserDrafts } from './draft-keys';
const STORAGE_KEY = 'aigovops:mock-user';
export const PROFILE_STORAGE_KEY = 'aigovops:profile-id';

// Vite inlines this at build time. Empty string ⇒ mock mode.
const WORKOS_CLIENT_ID = (import.meta.env.VITE_WORKOS_CLIENT_ID ?? '').trim();
const WORKOS_MODE = WORKOS_CLIENT_ID.length > 0;

// `import.meta.env.PROD` is true when the SPA was built via `vite build`
// (i.e. shipped to a Worker), false during `vite dev`. Combined with
// WORKOS_MODE, this decides whether the mock backdoor is allowed at all.
//
// Rule: a production build MUST have WorkOS. If VITE_WORKOS_CLIENT_ID is
// missing at build time, the mock is refused — sign-in becomes a visible
// error page instead of an "any email works" backdoor. Dev builds continue
// to fall back to the mock so contributors can iterate offline.
const IS_PROD_BUILD = import.meta.env.PROD;
const AUTH_DISABLED = IS_PROD_BUILD && !WORKOS_MODE;

if (AUTH_DISABLED && typeof console !== 'undefined') {
  // Loud enough to surface in any operator's log tail. This is the diagnostic
  // pointer for anyone wondering why /signin renders 'auth unavailable.'
  console.error(
    '[auth] production build shipped without VITE_WORKOS_CLIENT_ID; sign-in is disabled. See docs/workos-setup.md.'
  );
}

// ---------------------------------------------------------------- Mock mode

function MockAuthProvider({ children }: { children: ReactNode }) {
  // Defense-in-depth. AUTH_DISABLED already prevents the ternary in
  // AuthProvider from picking this branch in a production build; combined
  // with Vite's `import.meta.env.PROD` inlining the whole function body
  // is dead code in prod bundles. This throw is here so that if any of
  // the above assumptions ever break (bundler regression, refactor that
  // drops the AUTH_DISABLED gate, staging build that ships with PROD=true
  // but no WorkOS config), the failure is a loud crash, not a silent
  // any-email backdoor. See M0.4 in docs/users-onboarding-billing-plan.md.
  if (import.meta.env.PROD) {
    throw new Error(
      '[auth] MockAuthProvider reached in production build. This is a bug: production must use WorkOS or the disabled provider. Do NOT paper over — investigate build config.'
    );
  }
  const [user, setUser] = useState<MockUser | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as MockUser) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (user) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    else window.localStorage.removeItem(STORAGE_KEY);
  }, [user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading: false,
      signIn: (email, name) => {
        const safeEmail = email ?? 'demo@example.com';
        setUser({
          id: `user-${btoa(safeEmail).replace(/=+$/, '').slice(0, 12)}`,
          email: safeEmail,
          name: name ?? safeEmail.split('@')[0],
          role: 'admin',
        });
      },
      signUp: (email, name) => {
        // Mock mode has no distinct sign-up path; delegate to signIn.
        const safeEmail = email ?? 'demo@example.com';
        setUser({
          id: `user-${btoa(safeEmail).replace(/=+$/, '').slice(0, 12)}`,
          email: safeEmail,
          name: name ?? safeEmail.split('@')[0],
          role: 'admin',
        });
      },
      signOut: () => {
        clearUserDrafts();
        setUser(null);
      },
      setProfileId: (profileId, slug) =>
        setUser((u) => (u ? { ...u, profile_id: profileId, ...(slug !== undefined ? { profile_slug: slug } : {}) } : u)),
    }),
    [user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// -------------------------------------------------------------- WorkOS mode

type MeResponse = {
  user: {
    user_id: string;
    email: string;
    display_name: string;
    role: 'admin' | 'editor' | 'viewer';
  };
  default_workspace?: {
    workspace_id: string;
    slug: string;
    name: string;
  } | null;
};

function WorkosAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MockUser | null>(null);
  const [loading, setLoading] = useState(true);

  const readStoredProfileId = useCallback((): string | undefined => {
    if (typeof window === 'undefined') return undefined;
    return window.localStorage.getItem(PROFILE_STORAGE_KEY) ?? undefined;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/auth/me', {
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });
        if (cancelled) return;
        if (res.status === 401 || res.status === 403 || res.status === 503) {
          setUser(null);
          return;
        }
        if (!res.ok) {
          setUser(null);
          return;
        }

        const body = (await res.json()) as MeResponse;
        const cachedProfileId = readStoredProfileId();

        // Server-of-truth hydration for profile_id.
        //
        // profile_id was previously read from localStorage only. A fresh
        // browser (or cleared cache) then saw the user as "not-yet-
        // onboarded" and landed them in the wizard even though a
        // customer_profiles row exists for their workspace — the
        // multi-browser onboarding bug surfaced on 2026-08-24.
        //
        // We can't safely close that gap with a cross-tenant
        // `listProfiles()` — that shape returns every profile globally
        // to any authenticated caller and would render Customer A's
        // data for Customer B (see CLAUDE.md "Security posture").
        // Instead we hit workspace-scoped `GET /api/w/:slug/profile`,
        // gated by workspaceContext membership on the server.
        //
        // Done inline in this effect (not a follow-up useEffect) so
        // `loading` covers the whole resolution — RequireAuth's
        // synchronous `!user.profile_id → /onboarding` decision only
        // fires once hydration is final.
        // The server is authoritative — always ask it first when we
        // have a workspace slug, and fall back to the cached value only
        // when the server can't answer. This avoids the stale-cache
        // trap where an old localStorage profile_id survives a workspace
        // rebind (or was captured from a legacy pre-tenancy profile
        // whose workspace_id is now NULL). Without this, downstream
        // reads like getProfile(id) hit 404 under the new membership
        // gate and pages error out even though a valid profile exists
        // for the workspace.
        let hydratedProfileId: string | undefined;
        const slug = body.default_workspace?.slug;
        if (slug) {
          try {
            const pRes = await fetch(
              `/api/w/${encodeURIComponent(slug)}/profile`,
              {
                credentials: 'include',
                headers: { Accept: 'application/json' },
              }
            );
            if (!cancelled && pRes.ok) {
              const pBody = (await pRes.json()) as {
                profile?: { profile_id?: string } | null;
              };
              const pid = pBody.profile?.profile_id;
              if (pid) {
                hydratedProfileId = pid;
                if (typeof window !== 'undefined') {
                  window.localStorage.setItem(PROFILE_STORAGE_KEY, pid);
                }
              }
            }
            // Non-ok (401/403/404/500) → treat as "no profile"; wizard
            // will handle it. Don't fail the whole /me load on it.
          } catch (err) {
            console.error(
              '[auth] workspace profile hydration failed',
              err
            );
          }
        }
        // Fall back to the cached value only when the server didn't
        // give us one — e.g. workspace hydration failed for a transient
        // reason, or the user has no default_workspace (pre-tenancy).
        // Note: if the server explicitly returned {profile: null}, we
        // now drop the cached id — the cache is stale, and using it
        // downstream would trip the membership gate.
        if (!hydratedProfileId && !slug && cachedProfileId) {
          hydratedProfileId = cachedProfileId;
        }
        // Keep localStorage in sync with the server so a page refresh
        // (which reads localStorage before the /me effect finishes)
        // doesn't restore a stale id we just decided to drop.
        if (typeof window !== 'undefined') {
          if (hydratedProfileId) {
            window.localStorage.setItem(PROFILE_STORAGE_KEY, hydratedProfileId);
          } else if (slug) {
            // Server returned no profile for the workspace — drop the
            // stale cache so RequireAuth correctly bounces to onboarding.
            window.localStorage.removeItem(PROFILE_STORAGE_KEY);
          }
        }
        if (cancelled) return;

        setUser({
          id: body.user.user_id,
          email: body.user.email,
          name: body.user.display_name,
          role: body.user.role,
          profile_id: hydratedProfileId,
          profile_slug: slug ?? undefined,
          default_workspace: body.default_workspace ?? null,
        });
      } catch (err) {
        // Network error — treat as signed out.
        console.error('[auth] /api/auth/me failed', err);
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [readStoredProfileId]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      signIn: (email) => {
        // B5-7: if the user typed an email on /signin, pass it as
        // `login_hint` so WorkOS AuthKit skips the provider picker
        // and routes them straight to their configured IdP. Server
        // validates the hint's shape before forwarding to WorkOS.
        const trimmed = email?.trim();
        const params = new URLSearchParams();
        if (trimmed) params.set('login_hint', trimmed);
        const suffix = params.toString() ? `?${params.toString()}` : '';
        window.location.href = `/api/auth/authorize${suffix}`;
      },
      signUp: (email) => {
        // ADR-0015 · /signup delegates to /api/auth/authorize with
        // screen_hint=sign-up so WorkOS AuthKit opens on the sign-up
        // tab. Otherwise identical to signIn.
        const trimmed = email?.trim();
        const params = new URLSearchParams();
        if (trimmed) params.set('login_hint', trimmed);
        params.set('screen_hint', 'sign-up');
        window.location.href = `/api/auth/authorize?${params.toString()}`;
      },
      signOut: () => {
        clearUserDrafts();
        // Fire-and-forget POST — the browser will navigate to the 302 target.
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = '/api/auth/logout';
        document.body.appendChild(form);
        form.submit();
      },
      setProfileId: (profileId, slug) => {
        if (typeof window !== 'undefined') {
          if (profileId) window.localStorage.setItem(PROFILE_STORAGE_KEY, profileId);
          else window.localStorage.removeItem(PROFILE_STORAGE_KEY);
        }
        // V101-6-3: remember which workspace this profile belongs to.
        setUser((u) => (u ? { ...u, profile_id: profileId, ...(slug !== undefined ? { profile_slug: slug } : {}) } : u));
      },
    }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// -------------------------------------------------------------- Disabled mode

/**
 * DisabledAuthProvider — installed automatically for production builds
 * that shipped without WorkOS configuration. No user, no sign-in path,
 * signOut is a no-op. The SignIn page detects this via isAuthDisabled()
 * and renders an operator-facing 'auth unavailable' state.
 */
function DisabledAuthProvider({ children }: { children: ReactNode }) {
  const value = useMemo<AuthContextValue>(
    () => ({
      user: null,
      loading: false,
      signIn: () => {
        // Deliberately a no-op. The SignIn page shows the operator-facing
        // 'WorkOS not configured' message before the user gets here.
      },
      signUp: () => {
        /* no-op — matches signIn's disabled-mode contract */
      },
      signOut: () => {
        /* no-op */
      },
      setProfileId: () => {
        /* no-op */
      },
    }),
    []
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// --------------------------------------------------------------------- Exports

export function AuthProvider({ children }: { children: ReactNode }) {
  if (AUTH_DISABLED) return <DisabledAuthProvider>{children}</DisabledAuthProvider>;
  return WORKOS_MODE ? (
    <WorkosAuthProvider>{children}</WorkosAuthProvider>
  ) : (
    <MockAuthProvider>{children}</MockAuthProvider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Auth context or null (for components that may render outside AuthProvider, e.g. tests). */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}

export function useRequiredProfile(): string {
  const { user } = useAuth();
  if (!user?.profile_id) {
    throw new Error('No profile_id — user should be redirected to onboarding');
  }
  return user.profile_id;
}

/** Exposed so pages (SignIn, Landing) can adjust copy per mode. */
export function isWorkosMode(): boolean {
  return WORKOS_MODE;
}

/** True when a production build shipped without WorkOS — no auth path available. */
export function isAuthDisabled(): boolean {
  return AUTH_DISABLED;
}
